import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ShieldCheck, ArrowLeft, AlertTriangle, Loader2, MapPin, Globe } from "lucide-react";
import { supabase } from "../supabaseClient";
import { useIsAppAdmin } from "../hooks/useIsAppAdmin";

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

export default function CreateClub() {
  const navigate = useNavigate();
  const { isAdmin: canAccess, loading: checkingAccess } = useIsAppAdmin();

  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("France");

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

async function handleSubmit(e: React.FormEvent) {
  e.preventDefault();
  setErrorMsg(null);

  const cleanName = name.trim();
  const cleanCity = city.trim();
  const cleanCountry = country.trim();

  if (!cleanName) {
    setErrorMsg("Le nom du club est obligatoire.");
    return;
  }

  setSaving(true);

  try {
    const { data, error } = await supabase.rpc("create_club_with_owner", {
      _name: cleanName,
      _city: cleanCity || null,
      _country: cleanCountry || null,
    });

    if (error) throw error;
    if (!data?.id) throw new Error("Club créé mais identifiant introuvable.");

    navigate(`/club/${data.id}/members`);
  } catch (err: any) {
    setErrorMsg(err?.message || "Impossible de créer le club.");
  } finally {
    setSaving(false);
  }
}

  if (checkingAccess) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6 text-sm text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          Vérification des accès…
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 mt-0.5" />
            <div>
              <div className="font-semibold">Accès refusé</div>
              <div className="text-sm mt-1">
                Cette page est réservée aux administrateurs de l’application.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Espace club</div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Créer un club
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Crée ton club puis invite les autres membres depuis l’espace club.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
      </div>

      <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 md:p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-gray-100 dark:bg-gray-800 p-3">
            <Building2 className="h-6 w-6 text-gray-700 dark:text-gray-200" />
          </div>

          <div>
            <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Nouveau club
            </div>
            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Après création, tu seras automatiquement ajouté comme{" "}
              <span className="font-medium">dirigeant</span>.
            </div>

            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-yellow-50 px-3 py-1 text-xs text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
              <ShieldCheck className="h-3.5 w-3.5" />
              Accès complet au club après création
            </div>
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 md:p-6 space-y-5"
      >
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            Nom du club
          </label>
          <div className="mt-2 relative">
            <Building2 className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex. EFFR Basket"
              className="w-full pl-9 pr-3 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
              disabled={saving}
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            Ville
          </label>
          <div className="mt-2 relative">
            <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Ex. Firminy"
              className="w-full pl-9 pr-3 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
              disabled={saving}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            Pays
          </label>
          <div className="mt-2 relative">
            <Globe className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="Ex. France"
              className="w-full pl-9 pr-3 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
              disabled={saving}
            />
          </div>
        </div>

        {errorMsg && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-200">
            {errorMsg}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || !name.trim()}
            className={cx(
              "inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm font-medium",
              "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
              (saving || !name.trim()) && "opacity-60 cursor-not-allowed"
            )}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Building2 className="h-4 w-4" />
            )}
            {saving ? "Création..." : "Créer le club"}
          </button>
        </div>
      </form>
    </div>
  );
}